import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { Button } from '../../../../web/components-v2/Button';
import { Skeleton } from '../../../../web/components-v2/Skeleton';
import { styleSheet } from './styles';

interface IProps {
	isEmpty: boolean;
	error: unknown;
	isLoading: boolean;
	isFetching: boolean;
	isRefetchError: boolean;
	isFetchingNextPage: boolean;
	isFetchNextPageError: boolean;
	hasNextPage: boolean;
	refetch: () => void;
	fetchNextPage: () => void;
	loadingLabel: string;
	emptyText: string;
	errorText: string;
	header?: React.ReactNode;
	children: React.ReactNode;
}

export const InfiniteListStates: React.FC<IProps> = ({
	isEmpty,
	error,
	isLoading,
	isFetching,
	isRefetchError,
	isFetchingNextPage,
	isFetchNextPageError,
	hasNextPage,
	refetch,
	fetchNextPage,
	loadingLabel,
	emptyText,
	errorText,
	header,
	children,
}) => {
	if (isLoading) {
		return (
			<div className={css(styleSheet.list)} aria-label={loadingLabel}>
				{[0, 1, 2, 3].map(index => (
					<Skeleton key={index} styles={[styleSheet.skeletonRow]} />
				))}
			</div>
		);
	}

	const retry = (
		<Button type='button' variant='outline' size='sm' isLoading={isFetching} onClick={() => refetch()}>
			Try again
		</Button>
	);

	if (error && isEmpty) {
		return (
			<div className={css(styleSheet.message)} role='alert'>
				<span>{errorText}</span>
				{retry}
			</div>
		);
	}
	if (isEmpty) {
		return <div className={css(styleSheet.message)}>{emptyText}</div>;
	}

	const loadMore = () => {
		if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
			fetchNextPage();
		}
	};

	return (
		<>
			{header}
			<div className={css(styleSheet.scroller)}>
				{isRefetchError ? (
					<div className={css(styleSheet.loadMoreError)} role='alert'>
						<span>{errorText}</span>
						{retry}
					</div>
				) : null}
				{children}
				{isFetchingNextPage ? <Skeleton styles={[styleSheet.skeletonRow]} /> : null}
				{isFetchNextPageError ? (
					<div className={css(styleSheet.loadMoreError)} role='alert'>
						<span>We couldn&apos;t load more calls.</span>
						<Button type='button' variant='outline' size='sm' onClick={() => fetchNextPage()}>
							Try again
						</Button>
					</div>
				) : null}
				<Waypoint bottomOffset='-200px' onEnter={loadMore} />
			</div>
		</>
	);
};
